import {
    makeStyles 
} from '@mui/styles';
import {
    Box, Button, Typography, useTheme 
} from '@mui/material';
import CardContainer from '../components/cardContainer';
import Card from '../components/card';
import {
    useContext, useEffect, useMemo, useRef, useState 
} from 'react';
import NumberInputScreen from '../components/numberInput';
import ProgressBar from '../components/progressBar';
import dataDogLogger from '../../../lib/datadog';
import {
    DiagnosticFormContext 
} from '../../../contexts/form';
import {
    TypographyVariants, TypographyWeights 
} from '../../../figmaTheme';
import {
    useCookies 
} from 'react-cookie';

type Props = {
    position: number,
    forwardJump?: number
};

const useStyles = makeStyles((theme) => ({
    scrollableContainer: {
        overflow: 'scroll',
        scrollbarWidth: 'none',
        minHeight: '100%',
        width: '100vw',
        maxWidth: '692px',
        transform: 'translate(-24px, 0)',
        padding: '0 24px'
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        opacity: 0,
        animation: '$fadeIn 0.5s forwards 0.2s'
    },
    title: {
        position: 'relative',
        margin: '0 0 50px'
    },
    subtitle: {
        fontWeight: TypographyWeights.BOLD,
        margin: '0 0 16px'
    },
    error: {
        textAlign: 'center',
        color: theme.palette.error.main,
        height: '40px',
        margin: '8px 0 0'
    },
    navigation: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        columnGap: '8px',
        marginTop: '20px'
    },
    leftArrow: {
        marginRight: '52px',
    },
    rightArrow: {
        marginLeft: '52px',
    },
    circle: {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: theme.palette.secondary.main
    },
    arrowButton: {
        padding: '16px',
        borderRadius: '50%',
        minWidth: 0,
        lineHeight: 0
    },
    '@keyframes fadeIn': {
        '0%': {
            opacity: 0 
        },
        '100%': {
            opacity: 1 
        },
    }
}));

export const targetSubtitles = ['Drink Target', 'Dry Day Target'];

export const DrinkTargets = ({
    position,
    forwardJump
}: Props): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const drinksBaseline = useMemo(() => {
        return formContext.answers?.totalDrinksBaseline;
    }, [formContext.answers?.totalDrinksBaseline]);
    const dryDaysBaseline = useMemo(() => {
        return formContext.answers?.totalDryDaysBaseline;
    }, [formContext.answers?.totalDryDaysBaseline]);
    const classes = useStyles();
    const theme = useTheme();
    const [cookies] = useCookies();
    const [startTime, setStartTime] = useState(formContext.startTime);

    const [step, setStep] = useState(0);
    const [targets, setTargets] = useState<number[]>([]);
    const [value, setValue] = useState('');
    const [error, setError] = useState('');
    const [disablePrevious, setDisablePrevious] = useState(true);
    const [disableNext, setDisableNext] = useState(true);

    const inputRef = useRef(null);

    useEffect(() => {
        setDisablePrevious(step === 0);
        setValue(targets[step]?.toString() || '');
        formContext.fireTrackingEvents(formContext, step === 0 ? 'custom drink target' : 'custom dry day target', cookies);
    }, [step]);

    const onClickNext = () => {
        if (!value || error) return;
        setStartTime(new Date().getTime());
        const newTargets = targets;
        newTargets[step] = parseInt(value);
        setTargets(newTargets);

        if (step === 1) {
            formContext.setAnswers({
                targetTotalDrinks: targets[0],
                targetTotalDryDays: targets[1] 
            });

            if (inputRef.current) inputRef.current.blur();

            setTimeout(() => {
                formContext.goNext(forwardJump);
            }, 50);
            return;
        }

        setStep(step + 1);
        if (inputRef.current) inputRef.current.focus();
    };

    const onClickPrevious = () => {
        if (step === 0) return;
        setStartTime(null);

        const newTargets = targets;
        newTargets[step] = parseInt(value);
        setTargets(newTargets);

        setStep(step - 1);
        if (inputRef.current) inputRef.current.focus();
    };

    const onChange = (e) => {
        if (!e.target.value) {
            setValue('');
            setError('');
            return;
        }

        if (step === 0 && parseInt(e.target.value).toString() !== e.target.value) setError('Please round to nearest whole drink');
        else if (step === 0 && e.target.value >= drinksBaseline && drinksBaseline !== 0) setError(`Input a number lower than your baseline of ${drinksBaseline} drinks`);
        else if (step === 0 && e.target.value < 0) setError(`Input a number between 0 and ${drinksBaseline - 1} drinks`);
        else if (step === 1 && parseInt(e.target.value).toString() !== e.target.value) setError('Please round to nearest whole day');
        else if (step === 1 && e.target.value > 7) setError(`Input a number between ${dryDaysBaseline} and 7 days`);
        else if (step === 1 && e.target.value < dryDaysBaseline) setError(`Input a number equal to or higher than your baseline of ${dryDaysBaseline} dry days`);
        else setError('');

        setValue(e.target.value);
    };

    const onKeyPress = (e) => {
        if (!value || error) return;

        if (e.key === 'Enter') onClickNext();
    };

    const onClickBack = () => {
        if (step === 0) formContext.goBack();
        else onClickPrevious();
    };

    useEffect(() => {
        if (!value || error) setDisableNext(true);
        else setDisableNext(false);
    }, [value]);

    useEffect(() => {
        if (!startTime) return;

        const endTime = new Date().getTime();
        const diff = endTime - startTime;

        dataDogLogger(
            'info',
            `Diagnostic screen ${step === 0 ? 'custom drink target' : 'custom dry day target'} rendered in ${diff}ms`,
            {
                frontendMetric: diff,
                url: step === 0 ? 'custom drink target' : 'custom dry day target',
                rawData: {
                    formId: formContext.formId
                }
            }
        );
    }, [startTime]);

    useEffect(() => {
        if (!inputRef.current) return;

        const timeout = setTimeout(() => {
            inputRef.current.focus();
        }, 1000);

        return () => clearTimeout(timeout);
    }, [inputRef.current]);

    return (
        <CardContainer
            sectionKey={'habits'}
            goBack={onClickBack}
            hasGradient
        >
            <Card>
                <ProgressBar
                    currentPosition={position}
                    sectionKey={'habits'}
                />
                <Box className={classes.scrollableContainer}>
                    <Box className={classes.content}>
                        <Typography
                            variant={TypographyVariants.H3}
                            className={classes.title}
                            dangerouslySetInnerHTML={{
                                __html: `Great! Let us know your <span style="font-weight:${TypographyWeights.BOLD};color:${theme.palette.secondary.main}">desired weekly targets</span>, and we'll configure your plan to get you there.` 
                            }}
                        />
                        <Typography
                            variant={TypographyVariants.H6}
                            className={classes.subtitle}
                            dangerouslySetInnerHTML={{
                                __html: targetSubtitles[step] 
                            }}
                        />
                        <NumberInputScreen
                            sectionKey={'habits'}
                            inputRef={inputRef}
                            onChange={onChange}
                            onKeyPress={onKeyPress}
                            value={value}
                            maxWidth={97}
                        />
                        <Box className={classes.navigation}>
                            <Button
                                className={`${classes.leftArrow} ${classes.arrowButton}`}
                                style={{
                                    backgroundColor: disablePrevious ? theme.palette.neutral['100'] : theme.palette.secondary.main,
                                    cursor: disablePrevious ? 'not-allowed' : 'pointer' 
                                }}
                                onClick={onClickPrevious}
                            >
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M7.79497 1.90498C8.06834 2.17834 8.06834 2.62156 7.79497 2.89493L3.38995 7.29995H14.3C14.6866 7.29995 15 7.61335 15 7.99995C15 8.38655 14.6866 8.69995 14.3 8.69995H3.38995L7.79497 13.105C8.06834 13.3783 8.06834 13.8216 7.79497 14.0949C7.52161 14.3683 7.07839 14.3683 6.80503 14.0949L1.20503 8.49493C0.931658 8.22156 0.931658 7.77834 1.20503 7.50498L6.80503 1.90498C7.07839 1.63161 7.52161 1.63161 7.79497 1.90498Z"
                                        fill={theme.palette.white}
                                    />
                                </svg>
                            </Button>
                            <Box
                                className={classes.circle}
                            />
                            <Box
                                className={classes.circle}
                                style={{
                                    backgroundColor: step === 1 ? theme.palette.secondary.main : theme.palette.secondary.light 
                                }}
                            />
                            <Button
                                className={`${classes.rightArrow} ${classes.arrowButton}`}
                                style={{
                                    backgroundColor: disableNext ? theme.palette.neutral['100'] : theme.palette.secondary.main,
                                    cursor: disableNext ? 'not-allowed' : 'pointer' 
                                }}
                                onClick={onClickNext}
                            >
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 17 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M8.70503 14.095C8.43166 13.8216 8.43166 13.3784 8.70503 13.105L13.1101 8.69999L2.2 8.69999C1.8134 8.69999 1.5 8.38659 1.5 7.99999C1.5 7.61339 1.8134 7.29999 2.2 7.29999L13.1101 7.29999L8.70503 2.89496C8.43166 2.62159 8.43166 2.17838 8.70503 1.90501C8.97839 1.63164 9.42161 1.63164 9.69498 1.90501L15.295 7.50501C15.5683 7.77838 15.5683 8.22159 15.295 8.49496L9.69497 14.095C9.42161 14.3683 8.97839 14.3683 8.70503 14.095Z"
                                        fill={theme.palette.white}
                                    />
                                </svg>
                            </Button>
                        </Box>
                        <Typography
                            variant={TypographyVariants.H6}
                            className={classes.error}
                        >
                            {error}
                        </Typography>
                    </Box>
                    <Box
                        style={{
                            height: '300px' 
                        }}
                    />
                </Box>
            </Card>
        </CardContainer>
    );
};

export default DrinkTargets;
